import { Component, Inject, OnInit, OnDestroy  } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef
} from '@angular/material/legacy-dialog';
import { FraisMissionDetail } from '@domain/admin/frais/indemnites/frais-mission-detail';
import { GeographieSelectionPopinComponent, GeographieSelectionPopinData} from "@share/component/geographie-selection-popin/geographie-selection-popin.component";
import { TypeGeographie } from "@domain/geographie/typeGeographie";
import { PorteeGeographie } from "@share/component/autocomplete/options/geographie";
import { filter, takeUntil } from "rxjs/operators";
import { GeographieView } from "@domain/geographie/geographieView";
import { TranslateService } from '@ngx-translate/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { NiveauAlerte, Alerte } from '@domain/common/alerte/alerte';
import { FraisMissionFormUtils } from '../../frais-mission-form.utils';

export interface FraisMissionDuplicationPopinData {
    //Données source à dupliquer
    fraisMission: FraisMissionDetail;
    //Territoire courant du détail
    selectedGeographie: GeographieView | null;
    //État calculé depuis le détail
    isTauxSpecialFromTerritoire: boolean;
}

export interface FraisMissionDuplicationPopinResult {
    //Valeurs choisies dans la popin
    selectedGeographie: GeographieView;
    fraisMission: Partial<FraisMissionDetail>;
}

@Component({
    selector: 'frais-mission-duplication-popin',
    templateUrl: './frais-mission-duplication-popin.component.html',
    styleUrls: ['./frais-mission-duplication-popin.component.scss']
})
export class FraisMissionDuplicationPopinComponent implements OnInit, OnDestroy {
    //Modèle local des champs éditables
    public fraisMissionForm: Partial<FraisMissionDetail> = {};
    //Territoire local
    public selectedGeographie: GeographieView | null = null;
    //État readonly lié au territoire
    public isTauxSpecialFromTerritoire = false;
    //Snapshot initial pour détecter les modifications
    private initialState = '';
    //Subject pour la gestion de la destruction du composant
    private destroy$ = new Subject<void>();
    //État de validité du formulaire pour éviter les recalculs à chaque cycle
    public canSave$ = new BehaviorSubject<boolean>(false);
    //Énumération des niveaux d'alerte pour le template
    public NiveauAlerte = NiveauAlerte;
    //Alerte d'information affichée en haut de la popin
    public alerte: Alerte;

    constructor(
        private dialogRef: MatDialogRef<FraisMissionDuplicationPopinComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FraisMissionDuplicationPopinData,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {}

    public ngOnInit(): void {
        //Initialisation des 4 champs avec les valeurs de l'écran source
        this.selectedGeographie = this.data.selectedGeographie;
        this.isTauxSpecialFromTerritoire = this.data.isTauxSpecialFromTerritoire;
        this.fraisMissionForm = {
            periodeMin: this.data.fraisMission.periodeMin,
            periodeMax: this.data.fraisMission.periodeMax,
            hasTauxSpecial: this.data.fraisMission.hasTauxSpecial,
            libelleSpecial: this.data.fraisMission.libelleSpecial
        };

        //Capture de l'état initial pour bloquer la sauvegarde si rien ne change
        this.initialState = this.serializeState();

        //Initialisation de l'état de validité
        this.updateCanSave();

        //Initialisation de l'alerte d'information
        this.alerte = {
            niveau: NiveauAlerte.WARNING,
            message: this.translateService.instant('admin.frais.indemnites.fraisMissions.detail.duplication.message')
        };
    }

    /**
     * Destruction du composant
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Met à jour l'état de validité du formulaire
     */
    public updateCanSave(): void {
        const isValid = this.computeCanSave();
        this.canSave$.next(isValid);
    }

    /**
     * Calcule si le formulaire peut être sauvegardé
     */
    private computeCanSave(): boolean {
        //Territoire obligatoire
        if (!this.selectedGeographie) {
            return false;
        }

        //Validation métier de cohérence période
        if (!this.isPeriodeValide()) {
            return false;
        }

        //Si taux spécial coché, le libellé est obligatoire
        if (this.fraisMissionForm.hasTauxSpecial && !this.fraisMissionForm.libelleSpecial?.trim()) {
            return false;
        }

        //Au moins un des 4 champs doit avoir changé
        return this.serializeState() !== this.initialState;
    }


    /**
     * Sauvegarde et ferme la popin avec les données du formulaire
     * Ne fait rien si le formulaire n'est pas valide
     */
    public onSave(): void {
        if (!this.canSave$.value) {
            return;
        }

        this.dialogRef.close({
            selectedGeographie: this.selectedGeographie,
            fraisMission: this.fraisMissionForm
        } as FraisMissionDuplicationPopinResult);
    }

    /**
     * Annule et ferme la popin sans sauvegarder
     */
    public onCancel(): void {
        this.dialogRef.close();
    }

    /**
     * Gestion du changement de territoire depuis l'autocomplete
     * @param geographie La géographie sélectionnée ou null si vidée
     */
    public onTerritoireChange(geographie: GeographieView | null): void {
        this.applyTerritoireSelection(geographie);
        this.updateCanSave();
    }

    /**
     * Appelé lors du changement d'un champ du formulaire
     */
    public onFieldChange(): void {
        this.updateCanSave();
    }

    /**
     * Ouvre la popin de sélection de territoire
     * Permet de choisir un territoire parmi les types disponibles (Pays, Ville, Zone, Région)
     */
    openTerritoirePopin(): void {
        FraisMissionFormUtils.openTerritoirePopin(
            this.dialog,
            this.translateService,
            this.destroy$,
            (geo) => {
                this.applyTerritoireSelection(geo);
                this.updateCanSave();
            }
        );
    }

    /**
     * Applique la sélection de territoire
     * @param geographie La géographie sélectionnée ou null si vidée
     */
    private applyTerritoireSelection(geographie: GeographieView | null): void {
        this.selectedGeographie = geographie ?? null;
    }

    /**
     * Vérifie la validité de la période de validité
     * La période min doit être >= 1 et si max est défini, il doit être >= min
     * @returns true si la période est valide, false sinon
     */
    private isPeriodeValide(): boolean {
        return FraisMissionFormUtils.isPeriodeValide(
            this.fraisMissionForm.periodeMin,
            this.fraisMissionForm.periodeMax
        );
    }

    /**
     * Sérialise l'état actuel du formulaire pour comparaison
     * Permet de détecter si des modifications ont été apportées
     * @returns Une chaîne JSON représentant l'état du formulaire
     */
    private serializeState(): string {
        //Construit une clé unique pour le territoire basée sur son type et son ID
        const territoireKey = this.selectedGeographie
            ? `${this.selectedGeographie.type}_${this.selectedGeographie.id}`
            : null;

        //Retourne une représentation JSON de l'état du formulaire pour comparaison
        return JSON.stringify({
            territoire: territoireKey,
            periodeMin: this.fraisMissionForm.periodeMin ?? null,
            periodeMax: this.fraisMissionForm.periodeMax ?? null,
            hasTauxSpecial: !!this.fraisMissionForm.hasTauxSpecial,
            libelleSpecial: this.fraisMissionForm.libelleSpecial?.trim() || null
        });
    }

    /**
     * Ferme la popin via le comportement d'annulation existant
     */
    public onClose(): void {
        this.onCancel();
    }

    /**
     * Appelé lors du changement de la case taux spécial
     */
    onTauxSpecialChange(): void {
        this.fraisMissionForm.libelleSpecial = FraisMissionFormUtils.getLibelleSpecialOnChange(
            this.fraisMissionForm.hasTauxSpecial,
            this.fraisMissionForm.libelleSpecial
        );
        this.updateCanSave();
    }
}